import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import CustomSelect from "./index";
import { formatOptions } from "../../utils/Helpers";

/**
 * A presentational select with infinite-scroll pagination and debounced search.
 * All query state and fetching is owned by the parent — this component only
 * calls `onSearch` and `onLoadMore` callbacks.
 *
 * @param {Object}   props.name            - Formik field name
 * @param {string}   props.placeholder     - Placeholder text
 * @param {Array}    props.items           - Full list from Redux
 * @param {boolean}  props.hasMore         - From Redux
 * @param {Function} props.onSearch        - (inputValue) => void — debounced, fires on user typing
 * @param {Function} props.onLoadMore      - () => void — fires on scroll-to-bottom
 * @param {Function} props.formatItem      - (item) => ({ id, label, value })
 * @param {Function} props.onChange        - (selected) => void
 * @param {string}   props.value           - Currently selected value
 * @param {boolean}  props.disabled        - Disable the select
 * @param {string}   props.bgColor         - Background color
 * @param {boolean}  props.isAddNoneOption - Show "None" option
 * @param {boolean}  props.isRequired      - Show required asterisk
 * @param {Object}   props.style           - Inline style wrapper
 * @param {number}   props.debounceMs      - Search debounce ms (default 400)
 */
function InfiniteScrollSelect({
  name,
  placeholder,
  items = [],
  hasMore = false,
  onSearch,
  onLoadMore,
  formatItem,
  onChange,
  disabled = false,
  bgColor,
  isAddNoneOption = false,
  isRequired = false,
  isMenuRelative,
  style,
  debounceMs = 400
}) {
  const searchTimer = useRef(null);
  const loadingRef = useRef(false);

  // Clear loading flag when items change (response received)
  useEffect(() => {
    loadingRef.current = false;
  }, [items]);

  const handleInputChange = useCallback(
    (inputValue, { action }) => {
      // Only respond to user typing — ignore menu-close, set-value, etc.
      if (action !== "input-change") return;

      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        onSearch?.(inputValue);
      }, debounceMs);
    },
    [onSearch, debounceMs]
  );

  const handleMenuScrollToBottom = useCallback(() => {
    if (loadingRef.current) return;
    if (!hasMore) return;
    loadingRef.current = true;
    onLoadMore?.();
  }, [hasMore, onLoadMore]);

  const options = items.map(
    formatItem || ((item) => formatOptions(item, "id", "name"))
  );
  console.log("options at infiniteScroll", options);

  return (
    <CustomSelect
      name={name}
      options={options}
      placeholder={placeholder}
      bgColor={bgColor}
      isRequired={isRequired}
      isAddNoneOption={isAddNoneOption}
      isMenuRelative={isMenuRelative}
      disabled={disabled}
      style={style}
      handleSelectChange={onChange}
      onMenuScrollToBottom={handleMenuScrollToBottom}
      onInputChange={handleInputChange}
      isSearchable={true}
    />
  );
}

InfiniteScrollSelect.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  items: PropTypes.array,
  hasMore: PropTypes.bool,
  onSearch: PropTypes.func,
  onLoadMore: PropTypes.func,
  formatItem: PropTypes.func,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  bgColor: PropTypes.string,
  isAddNoneOption: PropTypes.bool,
  isRequired: PropTypes.bool,
  isMenuRelative: PropTypes.bool,
  style: PropTypes.object,
  debounceMs: PropTypes.number
};

export default InfiniteScrollSelect;
